import { Map as ImmutableMap, Record } from "immutable"
import _ from "lodash"

import {
  Idea,
  ID,
  WebSource,
  WebSourceFieldName,
  IdeaFieldName,
  Location,
  SourceType,
  FleetingIdea,
  HexColor,
  IdeaType,
  Project,
  EditableProjectFields,
  EditableIdeaFields,
  EditOriginator,
} from "./types"
import Hub from "../hub"

const k = ({ gx, gy }) => `${gx},${gy}`
const width = (i: Idea) => (i.type == "goal" ? 2 : 1)
const height = (i: Idea) => (i.type == "goal" ? 2 : 1)
const editedAtIdeaFields = new Set<IdeaFieldName>(["content", "memo", "title"])

const distance = (locA: Location, locB: Location) => {
  const dx = locA.gx - locB.gx
  const dy = locA.gy - locB.gy
  return Math.sqrt(dx * dx + dy * dy)
}

export interface IStore {
  ideas: ImmutableMap<ID, Idea>
  fleetingIdeas: ImmutableMap<ID, FleetingIdea>
  sources: ImmutableMap<ID, WebSource>
  projects: ImmutableMap<ID, Project>
  ideaInverseLocations: ImmutableMap<string, ID>
}

export type IdMappings = {
  ideas?: { [key: string]: ID }
  sources?: { [key: string]: ID }
  projects?: { [key: string]: ID }
}

export class Store extends Record<IStore>({
  ideas: ImmutableMap<ID, Idea>(),
  fleetingIdeas: ImmutableMap<ID, FleetingIdea>(),
  sources: ImmutableMap<ID, WebSource>(),
  projects: ImmutableMap<ID, Project>(),
  ideaInverseLocations: ImmutableMap<string, ID>(),
}) {}

class Model {
  store: Store
  hub: Hub | null
  nextOriginator: EditOriginator

  constructor(store: Store, hub?: Hub) {
    this.store = store
    this.hub = hub || null
  }

  replaceIds(mappings: IdMappings): void {
    const ideaMappings = mappings.ideas || {}
    const sourceMappings = mappings.sources || {}
    const projectMappings = mappings.projects || {}

    const replaceIdeaId = (id: ID | null | undefined): ID | null | undefined =>
      id == null ? id : (ideaMappings[String(id)] ?? id)
    const replaceSourceId = (id: ID | null | undefined): ID | null | undefined =>
      id == null ? id : (sourceMappings[String(id)] ?? id)
    const replaceProjectId = (id: ID | null | undefined): ID | null | undefined =>
      id == null ? id : (projectMappings[String(id)] ?? id)

    let ideas = ImmutableMap<ID, Idea>()
    this.store.ideas.forEach((idea, id) => {
      const newId = replaceIdeaId(id) as ID
      const content = replacePageLinkIdsInContent(idea.content, ideaMappings)
      ideas = ideas.set(newId, {
        ...idea,
        id: newId,
        content,
        sourceId: replaceSourceId(idea.sourceId) as ID | null,
        connections: idea.connections.map((connectionId) => replaceIdeaId(connectionId) as ID),
      })
    })

    let fleetingIdeas = ImmutableMap<ID, FleetingIdea>()
    this.store.fleetingIdeas.forEach((idea, id) => {
      const newId = replaceIdeaId(id) as ID
      fleetingIdeas = fleetingIdeas.set(newId, {
        ...idea,
        id: newId,
        content: replacePageLinkIdsInContent(idea.content, ideaMappings),
        sourceId: replaceSourceId(idea.sourceId) as ID | undefined,
      })
    })

    let sources = ImmutableMap<ID, WebSource>()
    this.store.sources.forEach((source, id) => {
      const newId = replaceSourceId(id) as ID
      sources = sources.set(newId, {
        ...source,
        id: newId,
        goalId: replaceIdeaId(source.goalId) as ID | undefined,
        literatureNotes: source.literatureNotes
          ? replacePageLinkIdsInContent(source.literatureNotes, ideaMappings)
          : source.literatureNotes,
      })
    })

    let projects = ImmutableMap<ID, Project>()
    this.store.projects.forEach((project, id) => {
      const newId = replaceProjectId(id) as ID
      projects = projects.set(newId, {
        ...project,
        id: newId,
        goalId: replaceIdeaId(project.goalId) as ID | null | undefined,
        ideaIds: project.ideaIds.map((ideaId) => replaceIdeaId(ideaId) as ID),
      })
    })

    this.store = this.store
      .set("ideas", ideas)
      .set("fleetingIdeas", fleetingIdeas)
      .set("sources", sources)
      .set("projects", projects)
      .set("ideaInverseLocations", ImmutableMap<string, ID>())

    this.store.ideas.forEach((idea) => this.addIdeaToInverseLocations(idea))
  }

  // IDEAS AND GOALS

  ideaCounts(): { idea: number; goal: number; fleeting: number } {
    const result = { idea: 0, goal: 0, fleeting: 0 }
    this.store.ideas.forEach((idea, _k) => {
      result[idea.type] += 1
    })
    result.fleeting = this.store.fleetingIdeas.size
    return result
  }

  getIdeasWithTitles(): Idea[] {
    return Array.from(this.store.ideas.values())
      .map((idea) => idea)
      .filter((idea) => idea.title)
  }

  getGoals(): Idea[] {
    return Array.from(this.store.ideas.values()).filter((i) => i.type === "goal")
  }

  getIdea(id: ID): Idea | null {
    const idea = this.store.ideas.get(id)
    if (!idea) return null
    return idea
  }

  getIdeaIdAt(location: Location): ID | null {
    return this.store.ideaInverseLocations.get(k(location)) || null
  }

  getIdeaAt(location: Location): Idea | null {
    const id = this.store.ideaInverseLocations.get(k(location))
    if (!id) return null
    return this.store.ideas.get(id) || null
  }

  fetchIdea(id: ID): Idea {
    const idea = this.store.ideas.get(id)
    if (idea == null) throw "Unknown idea " + id
    return idea
  }

  getGoal(id: ID): Idea | null {
    const idea = this.store.ideas.get(id)
    if (idea == null) return null
    if (idea.type != "goal") return null
    return idea
  }

  getNearestIdeasTo(location: Location | null): Idea[] {
    if (location == null) return []

    // find ideas that are close to the open location
    const nearestIdeas = Array.from(this.store.ideas.values())
      .sort((a, b) => {
        const aDist = distance(a.location, location!)
        const bDist = distance(b.location, location!)
        return aDist - bDist
      })
      .filter((idea) => {
        if (idea.location.gx == location?.gx && idea.location.gy == location?.gy) return false
        return true
      })
      .slice(0, 10)

    return nearestIdeas
  }

  getNearestGoal(loc: Location): Idea | undefined {
    return this.getNearestOfType(loc, "goal")
  }

  getNearestIdea(loc: Location): Idea | undefined {
    return this.getNearestOfType(loc, "idea")
  }

  getNearest(loc: Location): Idea | undefined {
    return this.getNearestOfType(loc, null)
  }

  getNearestOfType(loc: Location, type: IdeaType | null): Idea | undefined {
    return Array.from(this.store.ideas.values())
      .filter((idea) => {
        if (type !== null && idea.type !== type) return false
        if (idea.location.gx == loc.gx && idea.location.gy == loc.gy) return false
        return true
      })
      .sort((a, b) => {
        const aDist = distance(a.location, loc)
        const bDist = distance(b.location, loc)
        return aDist - bDist
      })[0]
  }

  getNearestAvailableLocationIgnoring(
    loc: Location,
    width: number,
    height: number,
    ignoreIdeas: ID[],
  ): Location {
    const result = { gx: loc.gx, gy: loc.gy }
    while (!this.isAvailableLocationIgnoring(result, width, height, ignoreIdeas)) result.gx += 1
    return result
  }

  isAvailableLocationIgnoring(
    loc: Location,
    width: number,
    height: number,
    ignoreIdeas: ID[],
  ): boolean {
    const locationsToCheck: Location[] = []
    for (let i = 0; i < width; i++)
      for (let j = 0; j < height; j++)
        locationsToCheck.push({
          gx: loc.gx + i,
          gy: loc.gy + j,
        })
    return locationsToCheck.every((loc) => {
      const idHere = this.getIdeaIdAt(loc)
      return idHere == null || ignoreIdeas.includes(idHere)
    })
  }

  createIdea(
    id: ID,
    idea: Idea,
    inProjectsWithIndex?: [ID, number][] | null,
    isGoalForSources?: ID[] | null,
  ): any {
    if (idea.type == "fleeting") throw "called wrong method to create fleeting idea"

    const newIdea = {
      ...idea,
      updatedAt: idea.updatedAt || idea.createdAt || new Date(),
      editedAt: idea.editedAt || idea.createdAt || idea.updatedAt || new Date(),
    }
    this.store = this.store.set("ideas", this.store.ideas.set(id, newIdea))
    this.addIdeaToInverseLocations(newIdea)
    if (inProjectsWithIndex) this.addIdeaToProjects(id, inProjectsWithIndex)
    if (isGoalForSources && idea.type === "goal") this.setGoalToSources(id, isGoalForSources)
    if (this.hub)
      this.hub.emit("model:idea-created", {
        id: id,
        idea: newIdea,
        originator: this.nextOriginator,
      })
  }

  removeIdeaFromInverseLocations(idea: Idea): void {
    const { gx, gy } = idea.location
    for (let i = 0; i < width(idea); i++)
      for (let j = 0; j < height(idea); j++)
        this.store = this.store.set(
          "ideaInverseLocations",
          this.store.ideaInverseLocations.delete(k({ gx: gx + i, gy: gy + j })),
        )
  }

  addIdeaToInverseLocations(idea: Idea): void {
    const { gx, gy } = idea.location
    for (let i = 0; i < width(idea); i++)
      for (let j = 0; j < height(idea); j++)
        this.store = this.store.set(
          "ideaInverseLocations",
          this.store.ideaInverseLocations.set(
            k({
              gx: gx + i,
              gy: gy + j,
            }),
            idea.id,
          ),
        )
  }

  setIdeaField(
    id: ID,
    field: IdeaFieldName,
    value: any,
    updatedAt = new Date(),
    editedAt?: Date,
  ): any {
    if (field === "content") {
      this.setIdeaContent(id, value, updatedAt, editedAt)
      return
    }
    const idea = this.store.ideas.get(id)
    if (idea == null) return
    const editedFields = editedAtIdeaFields.has(field) ? { editedAt: editedAt || updatedAt } : {}
    const newIdea = { ...idea, [field]: value, updatedAt, ...editedFields }
    this.store = this.store.set("ideas", this.store.ideas.set(id, newIdea))
    if (this.hub)
      this.hub.emit("model:idea-updated", {
        id: id,
        idea: newIdea,
        fields: { [field]: value, updatedAt, ...editedFields },
        originator: this.nextOriginator,
      })
  }

  setIdeaLastViewedAt(id: ID, viewedAt: Date): any {
    const idea = this.store.ideas.get(id)
    if (idea == null || idea.type === "fleeting") return

    const currentValue =
      idea.lastViewedAt instanceof Date
        ? idea.lastViewedAt
        : idea.lastViewedAt
          ? new Date(idea.lastViewedAt)
          : null
    if (currentValue && currentValue >= viewedAt) return

    const newIdea = { ...idea, lastViewedAt: viewedAt }
    this.store = this.store.set("ideas", this.store.ideas.set(id, newIdea))
    if (this.hub)
      this.hub.emit("model:idea-updated", {
        id,
        idea: newIdea,
        fields: { lastViewedAt: viewedAt },
        originator: this.nextOriginator,
      })
  }

  // Returns whether the connections were changed
  setIdeaContent(id: ID, value: string, updatedAt = new Date(), editedAt?: Date): boolean {
    const idea = this.store.ideas.get(id)
    if (idea == null) return false
    const beforeConnections = idea.connections.sort()
    const afterConnections = extractConnectionIdeaIdsFromPageLinksInContent(value).sort()
    const didConnectionsChange = !_.isEqual(beforeConnections, afterConnections)
    const newIdea: Idea = {
      ...idea,
      content: value,
      connections: afterConnections,
      updatedAt,
      editedAt: editedAt || updatedAt,
    }
    this.store = this.store.set("ideas", this.store.ideas.set(id, newIdea))
    if (this.hub) {
      const changedFields: EditableIdeaFields = {
        content: value,
        updatedAt: newIdea.updatedAt,
        editedAt: newIdea.editedAt,
      }
      if (didConnectionsChange) changedFields.connections = afterConnections
      this.hub.emit("model:idea-updated", {
        id: id,
        idea: newIdea,
        fields: changedFields,
        originator: this.nextOriginator,
      })

      if (didConnectionsChange)
        this.hub.emit("model:idea-connections-changed", {
          from: id,
          to: afterConnections,
        })
    }
    return didConnectionsChange
  }

  setIdeaSource(
    id: ID,
    sourceType: SourceType | null,
    sourceId: ID | null,
    updatedAt = new Date(),
  ): any {
    const idea = this.store.ideas.get(id)
    if (idea == null) return
    const newIdea = { ...idea, sourceType: sourceType, sourceId: sourceId, updatedAt }
    this.store = this.store.set("ideas", this.store.ideas.set(id, newIdea))

    if (sourceType === "web" && sourceId) {
      const source = this.store.sources.get(sourceId)
      if (source == null) return
      this.setSourceField(sourceId, "lastUsedAt", new Date())
    }

    if (this.hub)
      this.hub.emit("model:idea-updated", {
        id: id,
        idea: newIdea,
        fields: { sourceType: sourceType, sourceId: sourceId, updatedAt },
        originator: this.nextOriginator,
      })
  }

  colourIdeas(idColourPairs: [ID, HexColor][], updatedAt = new Date()): any {
    idColourPairs.forEach(([id, colour]) => {
      this.setIdeaField(id, "colour", colour, updatedAt)
    })
  }

  moveIdeas(ideaLocations: [ID, Location][], updatedAt = new Date()): any {
    const moving = ideaLocations.filter(([id, _location]) => this.store.ideas.has(id))
    // Lift every idea before putting any down, so ideas can move into cells
    // others in the same move are leaving.
    for (const [id, _location] of moving) {
      const idea = this.store.ideas.get(id)!
      if (idea.type === "fleeting") throw `Tried to move fleeting idea ${idea}`
      this.removeIdeaFromInverseLocations(idea)
    }
    for (const [id, location] of moving) {
      const idea = this.store.ideas.get(id)!
      const newLocation = { gx: location.gx, gy: location.gy }
      const newIdea = { ...idea, location: newLocation, updatedAt }
      this.addIdeaToInverseLocations(newIdea)
      this.store = this.store.set("ideas", this.store.ideas.set(id, newIdea))

      if (this.hub)
        this.hub.emit("model:idea-updated", {
          id: id,
          idea: newIdea,
          fields: { location: newLocation, updatedAt },
          originator: this.nextOriginator,
        })
    }
  }

  inverseConnections(toId: ID): ID[] {
    const result: ID[] = []
    this.store.ideas.forEach((idea, id) => {
      if (idea.connections.includes(toId)) result.push(id)
    })
    return result
  }

  deleteIdeas(ids: ID[]): void {
    const ideas: Idea[] = ids
      .map((id) => {
        const idea = this.store.ideas.get(id)
        if (!idea) return null
        this.store = this.store.set("ideas", this.store.ideas.delete(id))
        this.removeIdeaFromInverseLocations(idea)
        if (idea.type == "goal") this.removeGoalFromSources(idea)
        this.removeIdeaFromProjects(idea)
        return idea
      })
      .filter((i) => i != null) as Idea[]
    if (this.hub)
      this.hub.emit("model:ideas-deleted", {
        ids,
        ideas,
        originator: this.nextOriginator,
      })
  }

  setGoalToSources(goalId: ID, sourceIds: ID[]): void {
    sourceIds.forEach((sourceId: ID) => {
      this.setSourceField(sourceId, "goalId", goalId)
    })
  }

  removeGoalFromSources(goalIdea: Idea): void {
    if (goalIdea.type !== "goal") return

    this.getSourcesWithGoal(goalIdea.id).forEach((source) => {
      this.setSourceField(source.id, "goalId", null)
    })
  }

  addIdeaToProjects(id: ID, projectIndexes: [ID, number][]): void {
    projectIndexes.forEach(([projectId, index]) => {
      const project = this.fetchProject(projectId)
      this.setProjectFields(projectId, {
        ideaIds: [...project.ideaIds.slice(0, index), id, ...project.ideaIds.slice(index)],
      })
    })
  }

  removeIdeaFromProjects(idea: Idea): void {
    this.getProjectsForIdea(idea.id).forEach((project) => {
      this.setProjectFields(project.id, {
        ideaIds: project.ideaIds.filter((id) => id !== idea.id),
      })
    })
  }

  // FLEETING IDEAS

  getFleetingIdeas(): FleetingIdea[] {
    return Array.from(this.store.fleetingIdeas.values())
  }

  getFleetingIdea(id: ID): FleetingIdea | null {
    const idea = this.store.fleetingIdeas.get(id)
    if (idea == null) return null
    return idea
  }

  fetchFleetingIdea(id: ID): FleetingIdea {
    const idea = this.store.fleetingIdeas.get(id)
    if (idea == null) throw "Unknown fleeting idea " + id
    return idea
  }

  createFleetingIdea(id: ID, fleetingIdea: FleetingIdea): void {
    this.store = this.store.set(
      "fleetingIdeas",
      this.store.fleetingIdeas.set(id, { ...fleetingIdea }),
    )
    if (this.hub)
      this.hub.emit("model:fleeting-idea-created", {
        id,
        fleetingIdea,
        originator: this.nextOriginator,
      })
  }

  deleteFleetingIdea(id: ID): void {
    const fleetingIdea = this.store.get("fleetingIdeas").get(id)
    if (!fleetingIdea) return
    this.store = this.store.set("fleetingIdeas", this.store.fleetingIdeas.delete(id))
    if (this.hub)
      this.hub.emit("model:fleeting-idea-deleted", {
        id,
        fleetingIdea,
        originator: this.nextOriginator,
      })
  }

  // SOURCES

  getSources(): WebSource[] {
    return Array.from(this.store.sources.values())
  }

  getSourcesWithGoal(goalId: ID): WebSource[] {
    return Array.from(this.store.sources.values()).filter((source) => source.goalId === goalId)
  }

  countSources(): number {
    return this.store.sources.size
  }

  countIdeasWithSource(sourceId: ID): number {
    return Array.from(this.store.ideas.values()).filter((i) => i.sourceId === sourceId).length
  }

  countFleetingIdeasWithSource(sourceId: ID): number {
    return Array.from(this.store.fleetingIdeas.values()).filter((i) => i.sourceId === sourceId)
      .length
  }

  getFleetingIdeasFromSource(sourceId: ID): FleetingIdea[] {
    return Array.from(this.store.fleetingIdeas.values()).filter((i) => i.sourceId === sourceId)
  }

  getFleetingIdeasWithNoSource(): FleetingIdea[] {
    return Array.from(this.store.fleetingIdeas.values()).filter((i) => i.sourceId == null)
  }

  getIdeasFromSource(sourceId: ID): Idea[] {
    return Array.from(this.store.ideas.values()).filter((i) => i.sourceId === sourceId)
  }

  getSource(id: ID): WebSource | null {
    const source = this.store.sources.get(id)
    if (source == null) return null
    return source
  }

  getSourceByURL(url: URL): WebSource | null {
    for (const source of this.store.sources.values())
      if (source.url && source.url === url.toString()) return source
    return null
  }

  fetchSource(id: ID): WebSource {
    const source = this.store.sources.get(id)
    if (source == null) throw "missing source " + id
    return source
  }

  createSource(id: ID, source: WebSource, relatedIdeaIds: ID[]): void {
    this.store = this.store.set("sources", this.store.sources.set(id, { ...source }))
    relatedIdeaIds.forEach((ideaId) => {
      this.setIdeaSource(ideaId, "web", id)
    })
    if (this.hub)
      this.hub.emit("model:source-created", {
        id,
        source,
        originator: this.nextOriginator,
      })
  }

  setSourceField(id: ID, field: WebSourceFieldName, value: any): void {
    const source = this.store.sources.get(id)
    if (source == null) return
    const updatedSource = {
      ...source,
      [field]: value,
      updatedAt: new Date(),
    }
    this.store = this.store.set("sources", this.store.sources.set(id, updatedSource))
    if (this.hub)
      this.hub.emit("model:source-updated", {
        id,
        source: updatedSource,
        fields: { [field]: value },
        originator: this.nextOriginator,
      })
  }

  deleteSource(id: ID): void {
    const source = this.store.get("sources").get(id)
    if (!source) return
    this.store = this.store.set("sources", this.store.sources.delete(id))
    if (this.hub)
      this.hub.emit("model:source-deleted", {
        id,
        source,
        originator: this.nextOriginator,
      })
    const ideas = this.getIdeasFromSource(id)
    ideas.forEach((idea) => {
      this.setIdeaSource(idea.id, null, null)
    })
  }

  // PROJECTS

  getProject(id: ID): Project | null {
    return this.store.projects.get(id) || null
  }

  getProjects(): Project[] {
    return Array.from(this.store.projects.values())
  }

  fetchProject(id: ID): Project {
    const project = this.getProject(id)
    if (project == null) throw `Project ${id} not found`
    return project
  }

  getMostRecentProject(): Project | null {
    let result: Project | null = null
    this.store.projects.forEach((project) => {
      if (result == null || project.createdAt > result.createdAt) {
        result = project
      }
    })
    return result
  }

  getProjectsForIdea(id: ID): Project[] {
    const result: Project[] = []
    this.store.projects.forEach((project) => {
      if (project.ideaIds.includes(id)) result.push(project)
    })
    return result
  }

  createProject(id: ID, project: Project): void {
    this.store = this.store.set("projects", this.store.projects.set(id, { ...project }))
    if (this.hub)
      this.hub.emit("model:project-created", {
        id,
        project,
        originator: this.nextOriginator,
      })
  }

  setProjectFields(id: ID, fields: EditableProjectFields): void {
    const project = this.fetchProject(id)
    fields = { ...fields }
    if (fields.ideaIds) fields.ideaIds = _.uniq(fields.ideaIds)
    this.store = this.store.set(
      "projects",
      this.store.projects.set(id, {
        ...project,
        ...fields,
        updatedAt: new Date(),
      }),
    )
    if (this.hub)
      this.hub.emit("model:project-updated", {
        id,
        project,
        fields,
        originator: this.nextOriginator,
      })
  }

  deleteProject(id: ID): void {
    const project = this.store.get("projects").get(id)
    if (!project) return
    this.store = this.store.set("projects", this.store.projects.delete(id))
    if (this.hub)
      this.hub.emit("model:project-deleted", {
        id,
        project,
        originator: this.nextOriginator,
      })
  }
}

// ids are in the html in the form of <a data-type="page-link" data-id="***">..</a>
const extractConnectionIdeaIdsFromPageLinksInContent = (html: string): ID[] => {
  const result: ID[] = []
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, "text/html")
  const pageLinks = doc.querySelectorAll("a[data-type='page-link']")
  pageLinks.forEach((pageLink) => {
    const id = pageLink.getAttribute("data-id")
    if (id) result.push(id)
  })
  return result
}

const replacePageLinkIdsInContent = (html: string, ideaMappings: { [key: string]: ID }): string => {
  if (!html || Object.keys(ideaMappings).length === 0 || typeof DOMParser === "undefined")
    return html

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, "text/html")
  let changed = false

  doc.querySelectorAll("a[data-type='page-link']").forEach((pageLink) => {
    const id = pageLink.getAttribute("data-id")
    if (!id) return

    const replacement = ideaMappings[id]
    if (!replacement) return

    pageLink.setAttribute("data-id", String(replacement))
    changed = true
  })

  return changed ? doc.body.innerHTML : html
}

export default Model
